import { warmUpAgentSession } from "@/clients/streaming/agent-stream";
import { QueryKeys } from "@/constants/query-keys";
import { chatSessionIdAtom } from "@/stores/chat.store";
import { syncDeviceTimezone } from "@/utils/timezone/sync-device-timezone";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { memo, useEffect, useRef, type PropsWithChildren } from "react";

export const AppLifecycleProvider = memo(function AppLifecycleProvider({
  children,
}: PropsWithChildren) {
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn } = useAuth();
  const sessionId = useAtomValue(chatSessionIdAtom);

  const canWarm = isLoaded && !!isSignedIn;

  const warmStateRef = useRef({ canWarm, sessionId });
  warmStateRef.current = { canWarm, sessionId };

  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;
    if (!canWarm) return;
    warmUpAgentSession(sessionId);
  }, [canWarm, sessionId]);

  // Launch-time timezone report — runs on web too, unlike the warm-up above.
  useEffect(() => {
    if (!canWarm) return;
    void syncDeviceTimezone();
  }, [canWarm]);

  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;

    const handlePromise = App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) return;
      const { canWarm: currentCanWarm, sessionId: currentSessionId } = warmStateRef.current;
      // Resume can arrive before Clerk restores authentication, or after expiry.
      // Cached user data alone is not permission to dispatch private requests.
      if (!currentCanWarm) return;
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SUBSCRIPTION_STATUS] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SUBSCRIPTION_CUSTOMER_INFO] });

      warmUpAgentSession(currentSessionId);
      void syncDeviceTimezone();
    });

    return () => {
      void handlePromise.then((handle) => handle.remove());
    };
  }, [queryClient]);

  return <>{children}</>;
});
